import React from 'react';
import {
  TagIcon,
  PlayIcon,
  CircleStackIcon,
  CheckCircleIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { DocumentDetail, DocumentVersion } from '../types/documentVersion.types';
import type { ProcessingNotice } from '../hooks/useVersionActions';

type LegalTagSuggestion = {
  tag: string;
  reason: string;
  confidence: number;
  source: 'content' | 'workflow' | 'risk' | 'matter';
};

type ReviewSignal = {
  label: string;
  detail: string;
  tone: 'green' | 'amber' | 'red' | 'blue' | 'slate';
};

const signalToneClass = (tone: ReviewSignal['tone']) => {
  switch (tone) {
    case 'green':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200';
    case 'amber':
      return 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200';
    case 'red':
      return 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200';
    case 'blue':
      return 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200';
    default:
      return 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200';
  }
};

const noticeToneClass = (tone: ProcessingNotice['tone']) => {
  switch (tone) {
    case 'success':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200';
    case 'warning':
      return 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200';
    case 'error':
      return 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200';
    default:
      return 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200';
  }
};

const asSearchText = (document?: DocumentDetail | null, version?: DocumentVersion) => {
  const pieces = [
    document?.title,
    document?.type,
    document?.category_name,
    document?.category_type,
    document?.matter_description,
    document?.client_name,
    version?.original_name,
    version?.changes_description,
    document?.ai_metadata?.classification?.practice_area,
    ...(document?.ai_metadata?.classification?.tags || []),
  ];
  return pieces.filter(Boolean).join(' ').toLowerCase();
};

const addSuggestion = (
  suggestions: LegalTagSuggestion[],
  appliedTags: Set<string>,
  suggestion: LegalTagSuggestion,
) => {
  if (appliedTags.has(suggestion.tag.toLowerCase())) return;
  if (suggestions.some(existing => existing.tag.toLowerCase() === suggestion.tag.toLowerCase())) return;
  suggestions.push(suggestion);
};

const buildLegalTagSuggestions = (
  document?: DocumentDetail | null,
  currentVersion?: DocumentVersion,
  versions: DocumentVersion[] = [],
  appliedTags: string[] = [],
): LegalTagSuggestion[] => {
  const haystack = asSearchText(document, currentVersion);
  const appliedTagSet = new Set(appliedTags.map(tag => tag.toLowerCase()));
  const suggestions: LegalTagSuggestion[] = [];

  const has = (...terms: string[]) => terms.some(term => haystack.includes(term));
  const hasSignedVersion = versions.some(version => Boolean(version.checkin_metadata?.signature_audit));
  const hasTracking = versions.some(version => countTrackedRevisions(version) > 0);

  if (has('fee agreement', 'engagement', 'retainer')) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Fee Agreement', reason: 'Title or metadata indicates an attorney-client fee or engagement document.', confidence: 94, source: 'content' });
    addSuggestion(suggestions, appliedTagSet, { tag: 'Retainer Related', reason: 'Retainer language should drive billing and intake review.', confidence: 88, source: 'workflow' });
    addSuggestion(suggestions, appliedTagSet, { tag: hasSignedVersion ? 'Executed Copy' : 'Needs Signature', reason: hasSignedVersion ? 'Signature evidence exists on at least one version.' : 'Agreement-like document has no signature evidence yet.', confidence: hasSignedVersion ? 86 : 82, source: 'workflow' });
  }
  if (has('motion', 'complaint', 'petition', 'brief', 'declaration', 'affidavit', 'order', 'notice')) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Court Filing', reason: 'Document appears to be a pleading, motion, order, or filed court paper.', confidence: 90, source: 'content' });
  }
  if (has('discovery', 'interrogator', 'request for production', 'admission', 'subpoena', 'deposition')) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Discovery', reason: 'Discovery terms were found in document metadata.', confidence: 90, source: 'content' });
  }
  if (has('settlement', 'release', 'stipulation')) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Settlement', reason: 'Settlement or release terms should be easy to filter and review.', confidence: 88, source: 'content' });
  }
  if (has('opposing counsel', 'plaintiff', 'defendant', 'respondent', 'petitioner')) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Party / Caption Sensitive', reason: 'Party/caption data should be extracted into CoreFacts and checked for conflicts.', confidence: 84, source: 'risk' });
  }
  if (has('privileged', 'attorney-client', 'work product', 'confidential')) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Privileged / Confidential', reason: 'Privilege or confidentiality language appears in metadata.', confidence: 92, source: 'risk' });
  }
  if (has('template', 'sample', 'form no.', 'model form')) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Template Source', reason: 'Document appears to be a reusable form or template.', confidence: 82, source: 'workflow' });
  }
  if (has('invoice', 'billing', 'payment', 'trust account', 'deposit')) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Billing Impact', reason: 'Payment, trust, or billing language may affect finance workflows.', confidence: 86, source: 'matter' });
  }
  if (hasTracking) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Has Track Changes', reason: 'A checked-in version contains imported Word revision tracking.', confidence: 96, source: 'workflow' });
  }
  if (document?.matter_id) {
    addSuggestion(suggestions, appliedTagSet, { tag: 'Matter Linked', reason: 'This document is already linked to a matter.', confidence: 80, source: 'matter' });
  }

  return suggestions.sort((a, b) => b.confidence - a.confidence).slice(0, 8);
};

const countTrackedRevisions = (version?: DocumentVersion) => {
  const summary = version?.checkin_metadata?.tracking_summary as Record<string, any> | undefined;
  if (!summary) return 0;
  const direct = Number(summary.totalRevisions ?? summary.total_revisions ?? summary.revisionCount ?? summary.revision_count);
  if (Number.isFinite(direct) && direct > 0) return direct;
  return ['insertions', 'deletions', 'moves', 'formatting', 'comments', 'trackedInsertions', 'trackedDeletions']
    .reduce((sum, key) => sum + (Number(summary[key]) || 0), 0);
};

const buildReviewSignals = (
  document?: DocumentDetail | null,
  currentVersion?: DocumentVersion,
  versions: DocumentVersion[] = [],
): ReviewSignal[] => {
  const signals: ReviewSignal[] = [];
  const currentRevisionCount = countTrackedRevisions(currentVersion);
  const hasSignedVersion = versions.some(version => Boolean(version.checkin_metadata?.signature_audit));
  const latestVersionId = versions[0]?.version_id;
  const isLatest = !currentVersion || !latestVersionId || currentVersion.version_id === latestVersionId;

  signals.push({ label: 'EdgeLex analysis ready', detail: 'Use EdgeCite/Weaviate analysis and DMS metadata for grounded document work.', tone: 'blue' });

  if (currentRevisionCount > 0) {
    signals.push({ label: 'Track Changes present', detail: `${currentRevisionCount} tracked revision${currentRevisionCount === 1 ? '' : 's'} available for review/audit.`, tone: 'blue' });
  }
  if (document?.is_checked_out) {
    signals.push({ label: 'Checked out', detail: 'Another edit session may be active. Confirm check-in status before final use.', tone: 'amber' });
  }
  if (document?.working_copy_metadata?.unsaved_changes || document?.last_local_save_at) {
    signals.push({ label: 'Local working copy activity', detail: 'Local save metadata exists; confirm the official DMS version is current.', tone: 'amber' });
  }
  if (hasSignedVersion) {
    signals.push({ label: 'Signature evidence exists', detail: 'At least one version includes signature audit evidence.', tone: 'green' });
  }
  if (!isLatest) {
    signals.push({ label: 'Prior version selected', detail: 'You are reviewing a non-latest version; compare before applying tags to final work.', tone: 'blue' });
  }
  if ((currentVersion?.size_bytes || 0) > 10 * 1024 * 1024) {
    signals.push({ label: 'Large document', detail: 'Large files may need extra retrieval validation after indexing.', tone: 'slate' });
  }
  if (!document?.matter_id) {
    signals.push({ label: 'No matter linked', detail: 'Matter linkage improves retrieval scoping and downstream AI context.', tone: 'red' });
  }

  return signals.slice(0, 6);
};

interface ProcessingPanelProps {
  isProcessing: boolean;
  processingStatus: any;
  processingNotice?: ProcessingNotice | null;
  onClearProcessingNotice?: () => void;
  documentTags: string[];
  newTag: string;
  setNewTag: (tag: string) => void;
  onProcessDocument: () => Promise<void>;
  onAddTag: () => Promise<void>;
  onApplyTag: (tag: string) => Promise<void>;
  onRemoveTag: (tag: string) => Promise<void>;
  document?: DocumentDetail | null;
  versions?: DocumentVersion[];
  currentVersionId?: string;
  // EdgeLex legal preprocessing controls.
  runDefinedTerms: boolean;
  setRunDefinedTerms: (b: boolean) => void;
  runExactSearchIndex: boolean;
  setRunExactSearchIndex: (b: boolean) => void;
  runCaptionPartiesExtractor: boolean;
  setRunCaptionPartiesExtractor: (b: boolean) => void;
  persistCoreFacts: boolean;
  setPersistCoreFacts: (b: boolean) => void;
}

export const ProcessingPanel: React.FC<ProcessingPanelProps> = ({
  isProcessing,
  processingStatus,
  processingNotice,
  onClearProcessingNotice,
  documentTags,
  newTag,
  setNewTag,
  onProcessDocument,
  onAddTag,
  onApplyTag,
  onRemoveTag,
  document,
  versions = [],
  currentVersionId,
  runDefinedTerms,
  setRunDefinedTerms,
  runExactSearchIndex,
  setRunExactSearchIndex,
  runCaptionPartiesExtractor,
  setRunCaptionPartiesExtractor,
  persistCoreFacts,
  setPersistCoreFacts,
}) => {
  const currentVersion = currentVersionId
    ? versions.find((version) => version.version_id === currentVersionId)
    : versions[0];
  const legalTagSuggestions = buildLegalTagSuggestions(document, currentVersion, versions, documentTags);
  const reviewSignals = buildReviewSignals(document, currentVersion, versions);
  const appliedTagSet = new Set(documentTags.map(tag => tag.toLowerCase()));
  const applyAllSuggestedTags = async () => {
    for (const suggestion of legalTagSuggestions.slice(0, 5)) {
      if (!appliedTagSet.has(suggestion.tag.toLowerCase())) {
        await onApplyTag(suggestion.tag);
      }
    }
  };

  return (
    <div className="space-y-6">
      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">EdgeLex AI Readiness</h4>

      {processingNotice && (
        <div className={`rounded-lg border p-4 shadow-sm ${noticeToneClass(processingNotice.tone)}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              {processingNotice.tone === 'error' || processingNotice.tone === 'warning' ? (
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
              ) : (
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
              )}
              <div>
                <div className="font-semibold">{processingNotice.title}</div>
                <p className="mt-1 text-sm opacity-90">{processingNotice.message}</p>
                {processingNotice.detail && (
                  <p className="mt-2 text-xs opacity-75">{processingNotice.detail}</p>
                )}
              </div>
            </div>
            {onClearProcessingNotice && (
              <button
                type="button"
                onClick={onClearProcessingNotice}
                className="rounded p-1 opacity-60 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
                aria-label="Dismiss indexing notice"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <div className="flex items-center space-x-2 mb-3">
            <CircleStackIcon className="h-5 w-5 text-lex-accent" />
            <span className="font-medium text-gray-900 dark:text-white">Document Analysis Pipeline</span>
          </div>
          <div className="space-y-4">
            <div className="rounded-md border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/20 p-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-blue-700 dark:text-blue-300 font-semibold">
                  Retrieval authority
                </div>
                <p className="mt-1 text-xs text-blue-900 dark:text-blue-100">
                  EdgeLex stores document/version truth, legal classification, CoreFacts, and EdgeCite/Weaviate analysis for AI grounding.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-xs text-gray-600 dark:text-gray-300">EdgeLex legal enrichers</label>
              <div className="space-y-1 text-sm text-gray-800 dark:text-gray-200">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={runDefinedTerms} onChange={(e) => setRunDefinedTerms(e.target.checked)} />
                  <span>Defined Terms Index</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={runExactSearchIndex} onChange={(e) => setRunExactSearchIndex(e.target.checked)} />
                  <span>Whole‑Doc Exact Search Index</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={runCaptionPartiesExtractor} onChange={(e) => setRunCaptionPartiesExtractor(e.target.checked)} />
                  <span>Caption / Parties Extractor</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={persistCoreFacts} onChange={(e) => setPersistCoreFacts(e.target.checked)} />
                  <span title="Stores parties, court name, case number, document type, matter and client IDs into EdgeLex durable facts for AI context.">Persist CoreFacts after processing</span>
                </label>
              </div>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={onProcessDocument}
                disabled={isProcessing}
                className="flex-1 text-left px-3 py-2 text-sm text-white bg-lex-accent hover:opacity-90 rounded transition-colors disabled:opacity-50"
              >
                <PlayIcon className="h-4 w-4 inline mr-2" />
                {isProcessing ? 'Processing...' : 'Run EdgeLex processing'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center space-x-2">
              <TagIcon className="h-5 w-5 text-emerald-500" />
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Legal Tags & Review Signals</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Tags improve DMS filtering and give Lex better document context.
                </p>
              </div>
            </div>
            {legalTagSuggestions.length > 1 && (
              <button
                onClick={applyAllSuggestedTags}
                className="shrink-0 px-2 py-1 text-xs rounded border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                title="Apply the top suggested legal tags to this document."
              >
                Apply top
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-50/70 dark:bg-emerald-950/20 dark:border-emerald-900 p-3">
              <div className="flex items-center gap-2 mb-2">
                <SparklesIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />
                <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                  Suggested legal tags
                </span>
              </div>
              {legalTagSuggestions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {legalTagSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.tag}
                      onClick={() => onApplyTag(suggestion.tag)}
                      className="group inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-white dark:bg-gray-900 px-3 py-1 text-xs text-gray-800 dark:text-gray-100 hover:border-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
                      title={`${suggestion.reason} Confidence: ${suggestion.confidence}%`}
                    >
                      <span>{suggestion.tag}</span>
                      <span className="text-[10px] text-emerald-700 dark:text-emerald-300">{suggestion.confidence}%</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  No strong suggestions yet. Add a tag below or index the document so future AI passes have richer context.
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">Applied tags</span>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{documentTags.length} saved</span>
              </div>
              <div className="flex space-x-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                  placeholder="e.g. Privileged, Final Executed Copy"
                className="flex-1 px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent dark:bg-gray-700 dark:text-white"
                  onKeyDown={(e) => e.key === 'Enter' && onAddTag()}
              />
              <button
                onClick={onAddTag}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                Add
              </button>
            </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {documentTags.length > 0 ? documentTags.map((tag, index) => (
                  <span
                    key={`${tag}-${index}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-300/60 dark:border-gray-600"
                  >
                    {tag}
                    <button
                      onClick={() => onRemoveTag(tag)}
                      className="ml-1.5 text-gray-400 hover:text-red-600"
                      title={`Remove ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                )) : (
                  <span className="text-xs text-gray-500 dark:text-gray-400">No lawyer-applied tags yet.</span>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-3">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheckIcon className="h-4 w-4 text-blue-500" />
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Review signals
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {reviewSignals.map((signal) => (
                  <div
                    key={`${signal.label}-${signal.detail}`}
                    className={`rounded border px-2.5 py-2 text-xs ${signalToneClass(signal.tone)}`}
                  >
                    <div className="flex items-center gap-2 font-medium">
                      {signal.tone === 'red' || signal.tone === 'amber' ? (
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                      ) : (
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                      )}
                      {signal.label}
                    </div>
                    <div className="mt-0.5 opacity-80">{signal.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {processingStatus && (
        <div className="bg-lex-accent-soft p-4 rounded-lg">
          <h5 className="font-medium text-gray-900 dark:text-white mb-2">Processing Status</h5>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Provider:</span>
              <span className="font-mono uppercase">{processingStatus.provider || 'edgelex'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Status:</span>
              <span className="capitalize">{processingStatus.status}</span>
            </div>
            {processingStatus.version_id && (
              <div className="flex justify-between text-sm">
                <span>Version:</span>
                <span className="font-mono truncate max-w-[16rem]">{processingStatus.version_id}</span>
              </div>
            )}
            {processingStatus.message && (
              <p className="text-sm text-gray-700 dark:text-gray-300">{processingStatus.message}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
